import { ERROR_MESSAGES } from "../../shared/errors";
import { TERNARY_REGEX } from "../regex";
import { EnvxError, EnvxSchema } from "../types";
import { toBoolean } from "../utils";
import { readSystemEnv } from "../utils/system-env";
import { hasInterpolation, isTernary } from "./utils";

type EnvMap = Record<string, any>;

function getEnvVar(
  name: string,
  rawEnv: EnvMap,
  resolvedEnv: EnvMap,
  resolving: Set<string>,
  depth: number,
  type: "interpolation" | "ternary" = "interpolation"
): any {
  if (resolvedEnv[name] !== undefined) {
    return resolvedEnv[name];
  } else if (rawEnv[name] !== undefined) {
    return resolveValue(
      rawEnv[name],
      rawEnv,
      resolvedEnv,
      resolving,
      name,
      depth + 1
    );
  } else {
    const sysVal = readSystemEnv(name);
    if (sysVal !== undefined) {
      resolvedEnv[name] = sysVal;
      return sysVal;
    }
    throw new EnvxError(
      ERROR_MESSAGES.lib.resolver.interpolationFailed(name, type)
    );
  }
}

/**
 * Resolves environment variables by evaluating interpolations and conditional expressions.
 *
 * The function:
 * 1. Merges raw entries with schema defaults.
 * 2. Recursively resolves variable references.
 * 3. Detects circular references and throws an error if found.
 * 4. Validates required variables defined in the schema.
 *
 * @param entries - List of key-value pairs representing environment variables.
 * @param schema - Optional schema defining defaults and required flags.
 * @returns A map of resolved environment variables.
 * @throws EnvxError if circular dependency or max resolution depth is exceeded.
 */
export function resolveEnvx(
  entries: { key: string; value: any }[],
  schema?: EnvxSchema
): EnvMap {
  const rawEnv: EnvMap = {};
  const resolvedEnv: EnvMap = {};

  for (const entry of entries) {
    rawEnv[entry.key] = entry.value;
  }

  if (schema) {
    for (const key in schema) {
      if (rawEnv[key] === undefined && schema[key].default !== undefined) {
        rawEnv[key] = schema[key].default;
      }
    }
  }

  const resolving = new Set<string>();

  for (const key of Object.keys(rawEnv)) {
    resolvedEnv[key] = resolveValue(
      rawEnv[key],
      rawEnv,
      resolvedEnv,
      resolving,
      key,
      0
    );
  }

  if (schema) {
    for (const key in schema) {
      if (schema[key].required && rawEnv[key] === undefined) {
        // [THROW_ERR_MSG] => Throws error if a required env variable is missing and no default is defined.
        throw new EnvxError(
          ERROR_MESSAGES.lib.resolver.requiredMissingInResolve(key)
        );
      }
    }
  }
  return resolvedEnv;
}

/**
 * Resolves the value of an environment variable by replacing references
 * to other variables and evaluating conditional expressions.
 *
 * Stops and throws an error if a variable refers to itself directly or indirectly,
 * or if the resolution goes too deep to avoid infinite loops.
 *
 * @param rawValue The original value (may contain references or conditions).
 * @param rawEnv All raw environment variables as key-value pairs.
 * @param resolvedEnv Variables already resolved.
 * @param resolving The set of variables currently being resolved (used to detect cycles).
 * @param currentKey The key of the variable currently being processed.
 * @param depth How deep the recursion currently is (to limit max depth).
 * @returns The final resolved value with all references and conditions evaluated.
 * @throws EnvxError If circular references or maximum recursion depth are detected.
 */
function resolveValue(
  rawValue: any,
  rawEnv: EnvMap,
  resolvedEnv: EnvMap,
  resolving: Set<string>,
  currentKey: string,
  depth: number = 0
): any {
  if (typeof rawValue !== "string") return rawValue;

  // [circularDependency] => Detects circular dependencies to prevent infinite resolution loops.
  if (resolving.has(currentKey)) {
    throw new EnvxError(
      ERROR_MESSAGES.lib.resolver.circularDependency(currentKey)
    );
  }

  const MAX_DEPTH = 10;
  // [maxDepthExceeded] => Enforces a maximum recursion depth to prevent infinite resolution loops.
  if (depth > MAX_DEPTH) {
    throw new EnvxError(
      `[envx:error] Max resolution depth exceeded while resolving "${currentKey}".`
    );
  }

  resolving.add(currentKey);

  let result = rawValue;
  let prev: string;

  while (typeof result === "string") {
    prev = result;

    if (isTernary(result)) {
      result = resolveTernary(
        result,
        rawEnv,
        resolvedEnv,
        resolving,
        depth + 1
      );
      continue;
    }

    if (hasInterpolation(result)) {
      result = resolveInterpolation(
        result,
        rawEnv,
        resolvedEnv,
        resolving,
        depth + 1
      );
      continue;
    }

    if (result === prev) break;
  }

  resolving.delete(currentKey);
  return result;
}

function resolveInterpolation(
  str: string,
  rawEnv: EnvMap,
  resolvedEnv: EnvMap,
  resolving: Set<string>,
  depth: number
): string {
  return str.replace(/\${([^}]+)}/g, (_, varName) => {
    const name = varName.trim();

    return String(getEnvVar(name, rawEnv, resolvedEnv, resolving, depth));
  });
}

function resolveTernary(
  str: string,
  rawEnv: EnvMap,
  resolvedEnv: EnvMap,
  resolving: Set<string>,
  depth: number
): string {
  const cleaned = str?.trim();
  const match = cleaned.match(TERNARY_REGEX);

  if (!match) return str;

  const [
    ,
    varName,
    operator,
    ,
    compareValueRaw,
    ,
    trueValRaw,
    ,
    falseValRaw,
  ] = match;
  const name = varName?.trim();

  // [DEPRACTED]
  // const actualValue =
  //   resolvedEnv[name] !== undefined
  //     ? resolvedEnv[name]
  //     : resolveValue(
  //         rawEnv[name],
  //         rawEnv,
  //         resolvedEnv,
  //         resolving,
  //         name,
  //         depth + 1
  //       );

  const actualValue = getEnvVar(
    name,
    rawEnv,
    resolvedEnv,
    resolving,
    depth,
    "ternary"
  );

  let compareValue = compareValueRaw?.trim();
  if (
    (compareValue?.startsWith('"') && compareValue?.endsWith('"')) ||
    (compareValue?.startsWith("'") && compareValue?.endsWith("'"))
  ) {
    compareValue = compareValue.slice(1, -1);
  }

  const actualValStr = String(actualValue);

  let condition: boolean;
  // only accept operators => `==`, `!=`
  if (operator === "==" || operator === "!=") {
    const cmp = actualValStr === compareValue;
    condition = operator === "==" ? cmp : !cmp;
  } else {
    condition = toBoolean(actualValue);
  }

  const trueVal = trueValRaw.trim();
  const falseVal = falseValRaw.trim();
  return condition ? parseIfLiteral(trueVal) : parseIfLiteral(falseVal);
}

// ----------------------------------- //
// ---  Resolver helper functions  --- //
// ----------------------------------- //

function parseIfLiteral(value: string): any {
  if (value === "true") return true;
  if (value === "false") return false;
  if (!isNaN(Number(value))) return Number(value);
  return value;
}
